import { apiFetch } from './client';
import type { AddItemUnitBatchResult } from './masters';
import type { PurchaseBill, SavePurchaseBillInput } from './purchaseBills';
import type { PurchaseOrder, SavePurchaseOrderInput } from './purchaseOrders';
import type { SaveVendorPaymentInput, VendorPayment } from './vendorPayments';
import type { IntakeDraft } from '../units/intake';
import { toBatchRequest } from '../units/intake';
import { payInParts, type SplitPart } from '../cash/split';

/**
 * The purchase writes both clients send — the website's Purchases desk and the phone's — built
 * once, on `bookingCommands.ts`'s terms: an order, a delivery against it, their bill, and paying
 * them. The rules behind each are the server's.
 */

export function createPurchaseOrder(input: SavePurchaseOrderInput) {
  return apiFetch<PurchaseOrder>('/api/purchase-order', { method: 'POST', body: input });
}

/** Change, while nothing has come against it — the lines are replaced wholesale. */
export function updatePurchaseOrder(id: number, input: SavePurchaseOrderInput) {
  return apiFetch<PurchaseOrder>(`/api/purchase-order/${id}`, { method: 'PUT', body: input });
}

/** An order nothing has come against. */
export function cancelPurchaseOrder(id: number, reason: string) {
  return apiFetch<PurchaseOrder>(`/api/purchase-order/${id}/cancel`, {
    method: 'POST',
    body: { reason },
  });
}

/** A part-received order the rest of will not come. */
export function closePurchaseOrder(id: number, reason: string) {
  return apiFetch<PurchaseOrder>(`/api/purchase-order/${id}/close`, {
    method: 'POST',
    body: { reason },
  });
}

/** A delivery, exactly as Book in stock sends it (`units/intake.ts`). */
export function receiveDelivery(draft: IntakeDraft) {
  return apiFetch<AddItemUnitBatchResult>('/api/item-unit/batch', {
    method: 'POST',
    body: toBatchRequest(draft),
  });
}

export function createPurchaseBill(input: SavePurchaseBillInput) {
  return apiFetch<PurchaseBill>('/api/purchase-bill', { method: 'POST', body: input });
}

export function createVendorPayment(input: SaveVendorPaymentInput) {
  return apiFetch<VendorPayment>('/api/vendor-payment', { method: 'POST', body: input });
}

/**
 * Pay a supplier part by part (Pay B, `docs/Split_Payments_Plan.md`): one vendor payment per
 * account, each naming its own, in the order shown. The server sets each against the oldest bills,
 * so together they settle what one payment of the total would.
 */
export function payVendorInParts(vendorId: number, paidOn: string, parts: readonly SplitPart[]) {
  return payInParts(parts, (part, amount) =>
    createVendorPayment({
      vendorId,
      paidOn,
      amount,
      method: part.method,
      reference: part.reference.trim() || null,
      notes: null,
      allocations: null,
      cashAccountId: part.accountId,
    }),
  );
}
